import React, { useState, useEffect } from 'react';
import { Modal, Select, Table, Alert, Button, Space, Tag, Collapse, Typography, Divider, Statistic, Row, Col, Spin, Radio, Checkbox, Card } from 'antd';
import { CheckCircleOutlined, WarningOutlined, InfoCircleOutlined, SearchOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import { analyzeDuplicates, previewFileColumns } from '../api/customer';

const { Option } = Select;
const { Panel } = Collapse;
const { Text, Title } = Typography;

/**
 * FieldMappingModal Component
 * 
 * Allows users to map file columns to database fields
 * 
 * Props:
 * - visible: boolean - Show/hide modal
 * - fileColumns: array - Columns from uploaded file
 * - sampleData: array - Sample rows from file
 * - availableFields: object - Available database fields
 * - suggestedMapping: object - Auto-suggested mapping
 * - onConfirm: function - Callback when mapping is confirmed
 * - onCancel: function - Callback when modal is closed
 */
const FieldMappingModal = ({
  visible,
  fileColumns = [],
  sampleData = [],
  availableFields = {},
  suggestedMapping = {},
  filePreviewId = '',
  originalFile = null,
  onConfirm,
  onCancel
}) => {
  const [mapping, setMapping] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [duplicateAnalysis, setDuplicateAnalysis] = useState(null);
  const [analyzingDuplicates, setAnalyzingDuplicates] = useState(false);
  
  // Mapping mode states
  const [mappingMode, setMappingMode] = useState('position');
  const [hasHeader, setHasHeader] = useState(false);
  const [reloadingPreview, setReloadingPreview] = useState(false);
  const [currentColumns, setCurrentColumns] = useState(fileColumns);
  const [currentSampleData, setCurrentSampleData] = useState(sampleData);
  const [currentSuggestedMapping, setCurrentSuggestedMapping] = useState(suggestedMapping);

  // Initialize mapping with suggested values
  useEffect(() => {
    if (currentSuggestedMapping && Object.keys(currentSuggestedMapping).length > 0) {
      setMapping(currentSuggestedMapping);
    }
  }, [currentSuggestedMapping]);
  
  // Initialize current data from props
  useEffect(() => {
    setCurrentColumns(fileColumns);
    setCurrentSampleData(sampleData);
    setCurrentSuggestedMapping(suggestedMapping);
  }, [fileColumns, sampleData, suggestedMapping]);

  // Validate mapping whenever it changes
  useEffect(() => {
    validateMapping();
  }, [mapping]);

  const validateMapping = () => {
    const errors = [];
    const warnings = [];
    
    // Check required fields
    const requiredFields = availableFields.required_fields || [];
    const mappedDbFields = new Set(Object.values(mapping).filter(v => v));
    
    requiredFields.forEach(field => {
      if (!mappedDbFields.has(field)) {
        const fieldInfo = availableFields.all_fields?.[field];
        errors.push(`Required field "${fieldInfo?.label || field}" is not mapped`);
      }
    });

    // Check for duplicate mappings
    const fieldCounts = {};
    Object.values(mapping).forEach(dbField => {
      if (dbField) {
        fieldCounts[dbField] = (fieldCounts[dbField] || 0) + 1;
      }
    });

    Object.entries(fieldCounts).forEach(([dbField, count]) => {
      if (count > 1) {
        const fieldInfo = availableFields.all_fields?.[dbField];
        errors.push(`Field "${fieldInfo?.label || dbField}" is mapped multiple times`);
      }
    });

    // Check for unmapped optional fields
    if (availableFields.all_fields) {
      Object.entries(availableFields.all_fields).forEach(([fieldName, fieldInfo]) => {
        if (!fieldInfo.required && !mappedDbFields.has(fieldName)) {
          warnings.push(`Optional field "${fieldInfo.label}" is not mapped`);
        }
      });
    }

    setValidationErrors(errors);
    setValidationWarnings(warnings.slice(0, 5)); // Limit warnings
  };

  const handleMappingChange = (fileColumn, dbField) => {
    setMapping(prev => ({
      ...prev,
      [fileColumn]: dbField
    }));
  };

  const handleConfirm = () => {
    if (validationErrors.length === 0) {
      onConfirm(mapping);
    }
  };

  const handleAutoMap = () => {
    if (suggestedMapping) {
      setMapping(suggestedMapping);
    }
  };

  const handleClearMapping = () => {
    setMapping({});
    setDuplicateAnalysis(null);
  };
  
  const handleMappingModeChange = async (mode) => {
    setMappingMode(mode);
    if (mode === 'position') {
      setHasHeader(false);
    }
    await reloadPreview(mode, mode === 'position' ? false : hasHeader);
  };
  
  const handleHasHeaderChange = async (checked) => {
    setHasHeader(checked);
    await reloadPreview(mappingMode, checked);
  };
  
  const reloadPreview = async (mode, header) => {
    if (!originalFile) return;
    
    setReloadingPreview(true);
    setDuplicateAnalysis(null); // Clear duplicate analysis
    
    try {
      const preview = await previewFileColumns(originalFile, {
        mapping_mode: mode,
        has_header: header
      });
      
      setCurrentColumns(preview.columns || []);
      setCurrentSampleData(preview.sample_data || []);
      setCurrentSuggestedMapping(preview.suggested_mapping || {});
      
      // Apply new suggested mapping if available
      if (preview.suggested_mapping && Object.keys(preview.suggested_mapping).length > 0) {
        setMapping(preview.suggested_mapping);
      } else {
        setMapping({});
      }
    } catch (error) {
      console.error('Error reloading preview:', error);
    } finally {
      setReloadingPreview(false);
    }
  };

  const handleCheckDuplicates = async () => {
    if (validationErrors.length > 0) {
      return; // Don't check if mapping is invalid
    }

    setAnalyzingDuplicates(true);
    try {
      const analysis = await analyzeDuplicates(filePreviewId, mapping, 0.85);
      setDuplicateAnalysis(analysis);
    } catch (error) {
      console.error('Error analyzing duplicates:', error);
      // Handle error silently or show notification
    } finally {
      setAnalyzingDuplicates(false);
    }
  };

  // Prepare sample rows (up to 10)
  const sampleRows = currentSampleData.slice(0, 10);

  const isValid = validationErrors.length === 0;

  return (
    <Modal
      title={
        <Space>
          <InfoCircleOutlined />
          <span>Map File Columns to Database Fields</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={1200}
      style={{ top: 20 }}
      footer={[
      
        <Button
          key="check"
          onClick={handleCheckDuplicates}
          disabled={!isValid}
          loading={analyzingDuplicates}
          icon={<SearchOutlined />}
        >
          Check Duplicates
        </Button>,
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          disabled={!isValid}
          icon={isValid ? <CheckCircleOutlined /> : <WarningOutlined />}
        >
          Confirm & Upload
        </Button>
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Mapping Mode Configuration */}
        <Card size="small" title={<Space><SettingOutlined /> Column Mapping Mode</Space>}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Radio.Group 
                value={mappingMode} 
                onChange={(e) => handleMappingModeChange(e.target.value)}
                disabled={reloadingPreview}
              >
                <Radio value="position">Use Column Position (1, 2, 3...)</Radio>
                <Radio value="header">Use Column Names (Header Row)</Radio>
              </Radio.Group>
            </div>
            
            {mappingMode === 'header' && (
              <div>
                <Checkbox 
                  checked={hasHeader} 
                  onChange={(e) => handleHasHeaderChange(e.target.checked)}
                  disabled={reloadingPreview}
                >
                  First row contains column names
                </Checkbox>
              </div>
            )}
            
            <Alert
              message={
                mappingMode === 'header' 
                  ? hasHeader
                    ? "Using column names from the first row. The system will suggest field mappings."
                    : "First row will be treated as data. Columns will use positions."
                  : "Using column positions (Column 1, Column 2, etc.). You need to manually map each column."
              }
              type="info"
              showIcon
              style={{ fontSize: '12px' }}
              icon={reloadingPreview ? <Spin size="small" /> : <InfoCircleOutlined />}
            />
          </Space>
        </Card>
        {/* Validation Status */}
        {validationErrors.length > 0 && (
          <Alert
            message="Mapping Incomplete"
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            }
            type="error"
            showIcon
          />
        )}

        {validationErrors.length === 0 && !duplicateAnalysis && (
          <Alert
            message="Mapping Valid"
            description="All required fields are mapped correctly. Click 'Check Duplicates' to analyze the file."
            type="success"
            showIcon
          />
        )}

        {/* Duplicate Analysis Results */}
        {duplicateAnalysis && (
          <Alert
            message="Duplicate Analysis Complete"
            type="info"
            showIcon
            description={
              <Space direction="vertical" style={{ width: '100%' }}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic
                      title="Total Records"
                      value={duplicateAnalysis.total_records}
                      valueStyle={{ fontSize: '18px' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Unique Records"
                      value={duplicateAnalysis.unique_records}
                      valueStyle={{ fontSize: '18px', color: '#3f8600' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Duplicates"
                      value={duplicateAnalysis.duplicate_records}
                      valueStyle={{ fontSize: '18px', color: '#cf1322' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Duplicate Rate"
                      value={duplicateAnalysis.duplicate_rate}
                      precision={1}
                      suffix="%"
                      valueStyle={{ fontSize: '18px' }}
                    />
                  </Col>
                </Row>
                <Divider style={{ margin: '12px 0' }} />
                <Text>
                  <strong>{duplicateAnalysis.will_be_merged}</strong> duplicate records will be merged during upload.
                  Final record count will be <strong>{duplicateAnalysis.final_record_count}</strong>.
                </Text>
                {duplicateAnalysis.top_duplicate_clusters && duplicateAnalysis.top_duplicate_clusters.length > 0 && (
                  <Collapse ghost size="small">
                    <Panel header={`Top ${duplicateAnalysis.top_duplicate_clusters.length} Duplicate Groups`} key="clusters">
                      <ul style={{ margin: 0, paddingLeft: 20, fontSize: '12px' }}>
                        {duplicateAnalysis.top_duplicate_clusters.map((cluster, idx) => (
                          <li key={idx}>
                            <strong>{cluster.size}x</strong> - {cluster.name} | {cluster.address}, {cluster.city}, {cluster.state} {cluster.zip_code}
                          </li>
                        ))}
                      </ul>
                    </Panel>
                  </Collapse>
                )}
              </Space>
            }
          />
        )}

        {/* Mapping Table - Horizontal Layout */}
        <div>
          <Title level={5}>Column Mapping</Title>
          <Divider style={{ margin: '12px 0' }} />
          
          {/* Horizontal scrollable container */}
          <div style={{ 
            overflowX: 'auto', 
            overflowY: 'auto',
            maxHeight: '500px',
            border: '1px solid #f0f0f0', 
            borderRadius: '4px',
            position: 'relative'
          }}>
            <table style={{ width: '100%', minWidth: currentColumns.length * 200, borderCollapse: 'collapse' }}>
              {/* File Columns Row */}
              <thead>
                <tr style={{ backgroundColor: '#fafafa' }}>
                  {currentColumns.map((col, index) => (
                    <th 
                      key={col}
                      style={{
                        padding: '12px 8px',
                        borderRight: '1px solid #f0f0f0',
                        borderBottom: '2px solid #d9d9d9',
                        minWidth: '200px',
                        textAlign: 'left',
                        fontWeight: 600,
                        position: 'sticky',
                        top: 0,
                        backgroundColor: '#fafafa',
                        zIndex: 10
                      }}
                    >
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Space>
                          <Text strong style={{ fontSize: '13px' }}>{col}</Text>
                          {col.startsWith('Column_') && <Tag color="blue" style={{ fontSize: '11px' }}>Position</Tag>}
                        </Space>
                      </Space>
                    </th>
                  ))}
                </tr>
              </thead>
              
              {/* Database Field Mapping Row */}
              <tbody>
                <tr style={{ backgroundColor: '#fff' }}>
                  {currentColumns.map((col) => (
                    <td 
                      key={`mapping-${col}`}
                      style={{
                        padding: '8px',
                        borderRight: '1px solid #f0f0f0',
                        borderBottom: '2px solid #d9d9d9',
                        verticalAlign: 'top'
                      }}
                    >
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Select field"
                        value={mapping[col] || undefined}
                        onChange={(value) => handleMappingChange(col, value)}
                        showSearch
                        optionFilterProp="children"
                        allowClear
                        size="small"
                      >
                        {availableFields.groups && Object.entries(availableFields.groups).map(([groupName, fields]) => (
                          <Select.OptGroup key={groupName} label={groupName}>
                            {fields.map(field => (
                              <Option key={field.name} value={field.name}>
                                <Space size={4}>
                                  <span style={{ fontSize: '12px' }}>{field.label}</span>
                                  {field.required && <Tag color="red" style={{ fontSize: '10px', padding: '0 4px' }}>Required</Tag>}
                                </Space>
                              </Option>
                            ))}
                          </Select.OptGroup>
                        ))}
                      </Select>
                    </td>
                  ))}
                </tr>
                
                {/* Sample Data Rows (up to 10) */}
                {sampleRows.map((row, rowIndex) => (
                  <tr key={`row-${rowIndex}`} style={{ backgroundColor: rowIndex % 2 === 0 ? '#fafafa' : '#fff' }}>
                    {currentColumns.map((col) => (
                      <td 
                        key={`${col}-${rowIndex}`}
                        style={{
                          padding: '8px',
                          borderRight: '1px solid #f0f0f0',
                          borderBottom: '1px solid #f0f0f0',
                          fontSize: '12px',
                          color: '#595959',
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={row[col] || ''}
                      >
                        {row[col] || <Text type="secondary" style={{ fontSize: '11px' }}>(empty)</Text>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Sample rows info */}
          <div style={{ marginTop: '8px', textAlign: 'right' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Showing {sampleRows.length} sample row{sampleRows.length !== 1 ? 's' : ''}
            </Text>
          </div>
        </div>

        {/* Warnings */}
        {validationWarnings.length > 0 && (
          <Collapse ghost>
            <Panel
              header={
                <Space>
                  <WarningOutlined style={{ color: '#faad14' }} />
                  <Text>{validationWarnings.length} optional fields not mapped</Text>
                </Space>
              }
              key="warnings"
            >
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validationWarnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </Panel>
          </Collapse>
        )}

      </Space>
    </Modal>
  );
};

export default FieldMappingModal;
